import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import services from '../services/services';
import * as htmlToImage from 'html-to-image';
import jsPDF from 'jspdf';
import moment from 'moment';
import { imageDimensionsOnA4 } from './viewbooking.component';
import Swal from 'sweetalert2';

// Hour-meter readings are decimal hours written as HH:decimal (for example,
// 101:89 means 101.89 hours). Convert their difference to normal HH:MM.
const meterReadingDuration = (startReading, finishReading) => {
    const toDecimalHours = (reading) => {
        const match = String(reading || '').trim().match(/^(\d+)\s*:\s*(\d+)$/);
        return match ? Number(`${match[1]}.${match[2]}`) : null;
    };
    const start = toDecimalHours(startReading);
    const finish = toDecimalHours(finishReading);
    if (start === null || finish === null || finish < start) return null;

    const minutes = Math.round((finish - start) * 60);
    return { hours: String(Math.floor(minutes / 60)), minutes: String(minutes % 60).padStart(2, '0') };
};

const formatDateTimeWithAMPM = (val) => {
    if (!val) return { date: '', time: '' };
    const str = String(val).trim();
    if (!str) return { date: '', time: '' };

    const m = moment(str, [
        'DD-MM-YYYY hh:mm:ss A',
        'DD-MM-YYYY hh:mm A',
        'DD-MM-YYYY h:mm A',
        'YYYY-MM-DD hh:mm:ss A',
        'YYYY-MM-DD hh:mm A',
        'DD-MM-YYYY HH:mm:ss',
        'DD-MM-YYYY HH:mm',
        'YYYY-MM-DD HH:mm:ss',
        'YYYY-MM-DDTHH:mm:ss',
        moment.ISO_8601
    ], true);

    if (m.isValid()) {
        return { date: m.format('DD-MM-YYYY'), time: m.format('hh:mm A') };
    }

    const mFallback = moment(str);
    if (mFallback.isValid()) {
        return { date: mFallback.format('DD-MM-YYYY'), time: mFallback.format('hh:mm A') };
    }

    const parts = str.split(' ');
    return { date: parts[0] || '', time: parts.slice(1).join(' ') || '' };
};



const LogsheetPrintView = () => {
    let { id, bookingUniqueNo, companyId, clientId } = useParams();
    id = bookingUniqueNo || id;
    if (!id) {
        // Public email links are rendered outside <Routes>, so useParams()
        // is empty there. Read /logsheet/{BookingUniqueNo}
        // directly from the URL in that case.
        const parts = window.location.pathname.split('/').filter(Boolean);
        const logsheetIndex = parts.lastIndexOf('logsheet');
        const linkParts = logsheetIndex >= 0 ? parts.slice(logsheetIndex + 1) : [];

        if (linkParts.length >= 3) {
            companyId = linkParts[0];
            clientId = linkParts[1];
            id = linkParts[2];
        } else {
            id = linkParts[0] || '';
        }
    }
    const [data, setData] = useState(null);
    const [pdfUrl, setPdfUrl] = useState('');
    const [pdfFileName, setPdfFileName] = useState('');

    useEffect(() => {
        if (id) {
            fetchData(id);
        }
    }, [id]);

    const fetchData = (bookingUniqueNo) => {
        // The public link's final value is BookingUniqueNo.  This API returns
        // the complete logsheet record without a logged-in user or cookies.
        var detailReqData = JSON.stringify({ BookingUniqueNo: bookingUniqueNo });
        services.JsonValue({
            method: 'POST',
            url: services.API_URL + 'Report/getLogsheetDataByBookingUniqueNo',
            headers: { 'Content-Type': 'application/json' },
            data: detailReqData
        }).then(taskResponse => {
            // Public email links use /logsheet/{BookingUniqueNo}.
            // No login or browser cookies are required.
            let dynamicCompanyID = companyId || "";
            let dynamicClientID = clientId || "";
            let dynamicDate = "";
            let taskDetail = {};
            let taskBooking = {};

            const taskRows = taskResponse.data && taskResponse.data.data
                ? (Array.isArray(taskResponse.data.data) ? taskResponse.data.data : [taskResponse.data.data])
                : [];
            if (taskResponse.data && taskResponse.data.status && taskRows.length > 0) {
                const taskData = taskRows[0];
                taskBooking = taskData;
                taskDetail = taskData.BookingTaskDetail && taskData.BookingTaskDetail[0] ? taskData.BookingTaskDetail[0] : taskData;
                
                // Dynamically extract IDs and Date if they exist in the task response
                dynamicCompanyID = taskData.CompanyID || taskDetail.CompanyID || dynamicCompanyID;
                dynamicClientID = taskData.ClientID || taskDetail.ClientID || dynamicClientID;
                dynamicDate = taskData.BookingDate || taskDetail.BookingDate || "";
            }

            // This page intentionally calls only getLogsheetDataByBookingUniqueNo.
            Promise.resolve().then(() => {
                const response = {};
                const singleResponse = {};
                let rawBookingData = null;
                const singleRows = singleResponse.data && singleResponse.data.data
                    ? (Array.isArray(singleResponse.data.data) ? singleResponse.data.data : [singleResponse.data.data])
                    : [];
                const singleBooking = singleRows.find(b => b.BookingID == bookingUniqueNo || b.BookingNo == bookingUniqueNo) || (singleRows.length === 1 ? singleRows[0] : {});
                const multipleRows = response.data && response.data.data
                    ? (Array.isArray(response.data.data) ? response.data.data : [response.data.data])
                    : [];
                if (response.data && response.data.status && multipleRows.length > 0) {
                    // Find the matching booking from the array
                    const matchedBooking = multipleRows.find(b => b.BookingID == bookingUniqueNo || b.BookingNo == bookingUniqueNo);
                    // Only use a single returned row as a fallback.  With a
                    // multi-row response, showing the first row would attach
                    // another booking's data to this email link.
                    rawBookingData = matchedBooking || (multipleRows.length === 1 ? multipleRows[0] : null);
                }

                // If getMultipleBookingLogSheetData failed (e.g. missing CompanyID in Incognito mode), fallback to taskDetail
                if (!rawBookingData && Object.keys(taskDetail).length > 0) {
                    rawBookingData = taskDetail;
                }

            if (rawBookingData) {
                    // Replace literal "null" strings with empty strings
                    rawBookingData = JSON.parse(JSON.stringify(rawBookingData, (k, v) => v === 'null' ? '' : v));
                    taskDetail = JSON.parse(JSON.stringify(taskDetail, (k, v) => v === 'null' ? '' : v));

                    const bookingData = { ...singleBooking, ...rawBookingData };
                    // The parent booking has Zone/Ward/Municipality, while its
                    // nested task detail has operational readings.  Keep both.
                    const detail = { ...taskBooking, ...taskDetail, ...singleBooking };
                    const firstText = (...values) => values.find(value => String(value || '').trim() !== '') || '';

                    const totalTimeParts = (bookingData.TotalTime || detail.TotalTime || "").split(':');
                    bookingData.TotalHours = totalTimeParts[0] || "";
                    bookingData.TotalMinutes = totalTimeParts[1] || "";

                    const jettingParts = (bookingData.TotalJettingUsageTime || detail.TotalJettingUsageTime || "").split(':');
                    bookingData.JettingTotalHours = jettingParts[0] || "";
                    bookingData.JettingTotalMinutes = jettingParts[1] || "";

                    const suctionParts = (bookingData.TotalSuctionPumpUsageTime || detail.TotalSuctionPumpUsageTime || "").split(':');
                    bookingData.SuctionPumpTotalHours = suctionParts[0] || "";
                    bookingData.SuctionPumpTotalMinutes = suctionParts[1] || "";

                    bookingData.TotalLunchTime = bookingData.TotalLunchTime || detail.TotalLunchTime || ''; 
                    bookingData.Shift = bookingData.Shift || detail.Shift || '';
                    bookingData.TotalKiloMeter = bookingData.TotalKiloMeter || detail.TotalKiloMeter || '';
                    bookingData.TotalKilometer = bookingData.TotalKiloMeter; // Both spellings
                    bookingData.ParkingLocationName = bookingData.ParkingLocationName || detail.ParkingLocationName || '';
                    bookingData.BookingStartAddress = bookingData.BookingStartAddress || detail.BookingStartAddress || bookingData.Locality || detail.Locality || '';
                    bookingData.VehicleLeaveParkingTime = bookingData.VehicleLeaveParkingTime || detail.VehicleLeaveParkingTime || '';
                    bookingData.VehicleLeaveParkingTime2 = bookingData.VehicleLeaveParkingTime2 || bookingData.VehicleLeaveParkingTime || detail.VehicleLeaveParkingTime2 || detail.VehicleLeaveParkingTime || '';
                    bookingData.VehicleLeaveParkingOdometer = bookingData.VehicleLeaveParkingOdometer || detail.VehicleLeaveParkingOdometer || '';
                    bookingData.VehicleLeaveParkingKiloMeter = bookingData.VehicleLeaveParkingOdometer || bookingData.VehicleLeaveParkingKiloMeter || detail.VehicleLeaveParkingOdometer || detail.VehicleLeaveParkingKiloMeter || '';
                    bookingData.VehicleReachedLocationTime = bookingData.VehicleReachedLocationTime || detail.VehicleReachedLocationTime || '';
                    bookingData.VehicleReachedLocationTime2 = bookingData.VehicleReachedLocationTime2 || bookingData.VehicleReachedLocationTime || detail.VehicleReachedLocationTime2 || detail.VehicleReachedLocationTime || '';
                    bookingData.VehicleReachedLocationOdometer = bookingData.VehicleReachedLocationOdometer || detail.VehicleReachedLocationOdometer || '';
                    bookingData.VehicleReachedLocationKiloMeter = bookingData.VehicleReachedLocationOdometer || bookingData.VehicleReachedLocationKiloMeter || detail.VehicleReachedLocationOdometer || detail.VehicleReachedLocationKiloMeter || '';
                    bookingData.VehicleReturnLocationTime = bookingData.VehicleReturnLocationTime || detail.VehicleReturnLocationTime || '';
                    bookingData.VehicleReturnLocationTime2 = bookingData.VehicleReturnLocationTime2 || bookingData.VehicleReturnLocationTime || detail.VehicleReturnLocationTime2 || detail.VehicleReturnLocationTime || '';
                    bookingData.VehicleReturnLocationKiloMeter = bookingData.VehicleReturnLocationOdometer || bookingData.VehicleReturnLocationKiloMeter || detail.VehicleReturnLocationOdometer || detail.VehicleReturnLocationKiloMeter || '';
                    bookingData.VehicleReachedParkingTime = bookingData.VehicleReachedParkingTime || detail.VehicleReachedParkingTime || '';
                    bookingData.VehicleReachedParkingTime2 = bookingData.VehicleReachedParkingTime2 || bookingData.VehicleReachedParkingTime || detail.VehicleReachedParkingTime2 || detail.VehicleReachedParkingTime || '';
                    bookingData.VehicleReachedParkingKiloMeter = bookingData.VehicleReachedParkingOdometer || bookingData.VehicleReachedParkingKiloMeter || detail.VehicleReachedParkingOdometer || detail.VehicleReachedParkingKiloMeter || '';
                    bookingData.JettingStartMeterReading = bookingData.JettingStartMeterReading || detail.JettingStartMeterReading || '';
                    bookingData.JettingFinishMeterReading = bookingData.JettingFinishMeterReading || detail.JettingFinishMeterReading || '';
                    bookingData.SuctionPumpStartMeterReading = bookingData.SuctionPumpStartMeterReading || detail.SuctionPumpStartMeterReading || '';
                    bookingData.SuctionPumpFinishMeterReading = bookingData.SuctionPumpFinishMeterReading || detail.SuctionPumpFinishMeterReading || '';
                    const jettingDuration = meterReadingDuration(bookingData.JettingStartMeterReading, bookingData.JettingFinishMeterReading);
                    if (jettingDuration) {
                        bookingData.JettingTotalHours = jettingDuration.hours;
                        bookingData.JettingTotalMinutes = jettingDuration.minutes;
                    }
                    const suctionDuration = meterReadingDuration(bookingData.SuctionPumpStartMeterReading, bookingData.SuctionPumpFinishMeterReading);
                    if (suctionDuration) {
                        bookingData.SuctionPumpTotalHours = suctionDuration.hours;
                        bookingData.SuctionPumpTotalMinutes = suctionDuration.minutes;
                    }
                    bookingData.TotalDrainageMeter = bookingData.TotalMeter || bookingData.TotalDrainageMeter || detail.TotalMeter || detail.TotalDrainageMeter || '';
                    bookingData.TotalNoOfDranageLine = bookingData.TotalNoOfDranageLine || detail.TotalNoOfDranageLine || '';
                    bookingData.MachineNotWorkFromTime = bookingData.MachineNotWorkFromTime || detail.MachineNotWorkFromTime || '';
                    bookingData.MachineNotWorkToTime = bookingData.MachineNotWorkToTime || detail.MachineNotWorkToTime || '';
                    bookingData.MachineNotWorkTotalTime = bookingData.MachineNotWorkTotalTime || detail.MachineNotWorkTotalTime || '';
                    bookingData.MachineNotWorkFromTime2 = bookingData.MachineNotWorkFromTime2 || bookingData.MachineNotWorkFromTime || detail.MachineNotWorkFromTime2 || detail.MachineNotWorkFromTime || '';
                    bookingData.MachineNotWorkToTime2 = bookingData.MachineNotWorkToTime2 || bookingData.MachineNotWorkToTime || detail.MachineNotWorkToTime2 || detail.MachineNotWorkToTime || '';
                    bookingData.BookingCompleteRemarks = bookingData.BookingCompleteRemarks || detail.BookingCompleteRemarks || '';
                    bookingData.NoOfCleanMachineHole = bookingData.NoOfCleanMachineHole || detail.NoOfCleanMachineHole || '';
                    const reportDefects = bookingData.BookingDefectDetail;
                    const reportHasDefects = (Array.isArray(reportDefects) && reportDefects.length > 0) ||
                        (typeof reportDefects === 'string' && reportDefects.trim().length > 2 && reportDefects.trim() !== 'null');
                    bookingData.BookingDefectDetail = reportHasDefects
                        ? reportDefects
                        : detail.BookingDefectDetail || [];
                    bookingData.DriverName = bookingData.DriverFullName || bookingData.DriverName || detail.DriverFullName || detail.DriverName || '';
                    bookingData.AssignSupervisorFullName = bookingData.AssignSupervisorFullName || detail.AssignSupervisorFullName || '';
                    
                    if (bookingData.BookingTaskHelperDetail || detail.BookingTaskHelperDetail) {
                        const helpers = bookingData.BookingTaskHelperDetail || detail.BookingTaskHelperDetail;
                        bookingData.MultipleHelperName = helpers.map(h => h.FullName).join(', ');
                    } else {
                        bookingData.MultipleHelperName = '';
                    }
                    
                    bookingData.MuncipalityName = firstText(bookingData.MuncipalityName, bookingData.MunicipalityName, bookingData.Muncipality, bookingData.Municipality, detail.MuncipalityName, detail.MunicipalityName, detail.Muncipality, detail.Municipality, bookingData.Locality, detail.Locality);
                    bookingData.ZoneName = firstText(bookingData.ZoneName, detail.ZoneName);
                    bookingData.WardName = firstText(bookingData.WardName, detail.WardName);
                    bookingData.Locality = firstText(bookingData.Locality, detail.Locality);
                    bookingData.BookingDate = firstText(bookingData.BookingDate, detail.BookingDate);
                    bookingData.VehicleNo = firstText(bookingData.VehicleNo, detail.VehicleNo);

                    setData(bookingData);
                    setTimeout(() => {
                        generateImage(bookingData.BookingNo || bookingData.BookingID);
                    }, 50);

                } else {
                    Swal.fire({ position: 'top-end', toast: true, title: 'No Data Available', icon: 'error', showConfirmButton: false, timer: 1500 });
                }
            }).catch(err => {
                console.error(err);
            });
        }).catch(err => {
            console.error(err);
        });
    };

    const A4_PAPER_DIMENSIONS = { width: 210, height: 297 };

    const generateImage = async (fileName) => {
        const container = document.getElementById('divToNewPerformancePrint');
        if (!container) return;

        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
            (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        container.style.display = 'block';
        // The inner .page is the actual A4 sheet.
        const captureTarget = container.querySelector('.page') || container;
        if (isIOS) {
            // Capture outside the responsive page flow so WebKit uses the same
            // A4 measurements as the desktop Chrome download.
            container.style.position = 'fixed';
            container.style.left = '0';
            container.style.top = '0';
            container.style.zIndex = '-1';
            container.style.pointerEvents = 'none';
            container.style.width = '794px';
            captureTarget.style.width = '794px';
            captureTarget.style.margin = '0';
            captureTarget.style.boxSizing = 'border-box';
            captureTarget.style.webkitTextSizeAdjust = 'none';
            captureTarget.style.textSizeAdjust = 'none';
        }

        const resetCaptureLayout = () => {
            container.style.display = 'none';
            if (!isIOS) return;
            container.style.position = '';
            container.style.left = '';
            container.style.top = '';
            container.style.zIndex = '';
            container.style.pointerEvents = '';
            container.style.width = '';
            captureTarget.style.width = '';
            captureTarget.style.margin = '';
            captureTarget.style.boxSizing = '';
            captureTarget.style.webkitTextSizeAdjust = '';
            captureTarget.style.textSizeAdjust = '';
        };

        // Let the hidden print layout calculate its A4 dimensions and wait for
        // the Gujarati font before html-to-image measures the content.
        await new Promise((resolve) => requestAnimationFrame(resolve));
        if (document.fonts && document.fonts.ready) {
            await document.fonts.ready;
        }

        const sourceWidth = captureTarget.clientWidth;
        const sourceHeight = captureTarget.clientHeight;

        const imageOptions = {
            quality: 1,
            backgroundColor: '#FFFFFF',
            width: sourceWidth,
            height: sourceHeight,
            // Maximum JPEG quality keeps Gujarati text clear; two-times
            // resolution cuts the rendering work by more than half.
            pixelRatio: isIOS ? 2 : 3,
            cacheBust: false
        };

        htmlToImage.toJpeg(captureTarget, imageOptions)
            .then(function (dataUrl) {
                resetCaptureLayout();
                const doc = new jsPDF('p', 'mm', 'a4');
                doc.addImage(
                    dataUrl,
                    'JPEG',
                    0,
                    0,
                    210,
                    297,
                    'logsheet',
                    'FAST'
                );
                const pdfURL = doc.output('bloburl');
                const downloadFileName = 'Logsheet_' + fileName + '.pdf';
                setPdfUrl(pdfURL);
                setPdfFileName(downloadFileName);

                let alink = document.createElement('a');
                alink.href = pdfURL;
                alink.download = downloadFileName;
                document.body.appendChild(alink);
                alink.click();
                document.body.removeChild(alink);

                // The hidden A4 copy is briefly made visible for capture.
                // Return the website preview to its first line afterwards.
                window.scrollTo(0, 0);

                // Keep /logsheet/:id in the browser address bar.  The PDF is
                // displayed below in an embedded viewer instead of replacing
                // this route with a temporary blob: URL.
            }).catch(function (error) {
                resetCaptureLayout();
                console.error('Error processing image:', error);
            });
    };

    if (!data) {
        return <div>Loading...</div>;
    }

    const isEmailCondition = window.location.pathname.includes('/logsheet/') || window.location.search.includes('email');
    const firstNonEmpty = (...values) => values.find(value => String(value || '').trim() !== '') || '';
    const municipalityName = firstNonEmpty(
        data.MuncipalityName,
        data.MunicipalityName,
        data.Muncipality,
        data.Municipality,
        data.Locality
    );
    const normaliseChecklistTitle = value => String(value || '')
        .toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    const requiredPpe = [
        'safety shoes', 'hand gloves', 'helmet', 'mask',
        'light reflective jacket', 'uniform', 'i card'
    ];
    const checklistDetails = Array.isArray(data.BookingTaskCheckListDetail)
        ? data.BookingTaskCheckListDetail
        : [];
    const selectedPpe = checklistDetails
        .map(item => normaliseChecklistTitle(item.CheckListTitle));
    // Completion data can come from the newer Is* fields or the older form
    // fields. Keep an unanswered value blank; it must not be shown as "No".
    const yesNoAnswer = (...values) => {
        const value = values.find(item => item !== null && item !== undefined && String(item).trim() !== '');
        const answer = String(value ?? '').trim().toLowerCase();
        if (['yes', 'true', '1', 'હા'].includes(answer)) return 'yes';
        if (['no', 'false', '0', 'ના'].includes(answer)) return 'no';
        return '';
    };
    const yesNoText = (...values) => {
        const answer = yesNoAnswer(...values);
        return answer === 'yes' ? 'હા' : answer === 'no' ? 'ના' : '';
    };
    const renderYesNoChoices = (...values) => {
        const answer = yesNoAnswer(...values);
        return <span style={{ whiteSpace: 'nowrap' }}>
            <span className={`checkbox ${answer === 'yes' ? 'checked' : ''}`}></span> હા
            <span className={`checkbox ${answer === 'no' ? 'checked' : ''}`}></span> ના
        </span>;
    };
    const vehicleGvw = String(firstNonEmpty(
        data.GVW, data.VehicleGVW, data.VehicleCapacity, data.VehicleWeight
    )).toLowerCase();
    const isSevenTon = vehicleGvw.includes('7.0') || vehicleGvw.includes('7t');
    const isEighteenPointFiveTon = !isSevenTon;
    let bookingDefects = data.BookingDefectDetail;
    if (typeof bookingDefects === 'string') {
        try {
            bookingDefects = JSON.parse(bookingDefects);
        } catch (error) {
            bookingDefects = [];
        }
    }
    if (!Array.isArray(bookingDefects)) bookingDefects = [];
    const lastDefectRemark = bookingDefects.length
        ? bookingDefects[bookingDefects.length - 1].Remarks
        : '';
    const leaveOdometer = parseFloat(data.VehicleLeaveParkingOdometer);
    const reachedOdometer = parseFloat(data.VehicleReachedLocationOdometer);
    const totalDifferenceOdometer = Number.isFinite(leaveOdometer) && Number.isFinite(reachedOdometer)
        ? reachedOdometer - leaveOdometer
        : data.TotalDifferenceOdometer;
    const renderCertificate = (isPrintVersion = false) => (
        <div id={isPrintVersion ? "divToNewPerformancePrint" : undefined} className="divToNewPerformancePrint page-layout-gudm sm-techno-certificate" style={isPrintVersion ? { display: 'none' } : {}}>
            <style>
                {`
                                            @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Gujarati:wght@400;700&display=swap');
                                            .page-layout-gudm * { box-sizing: border-box; }
                                            .page-layout-gudm {
                                                margin: 0;
                                                background: #fff;
                                                font-family: "Noto Sans Gujarati", "Shruti", "Nirmala UI", Arial, sans-serif;
                                            }
                                            .page-layout-gudm .page {
                                                width: 210mm;
                                                min-height: 297mm;
                                                padding: 10mm 15mm;
                                                margin: 10px auto;
                                                background: #fff;
                                                font-size: 11.5pt;
                                                line-height: 1.8;
                                                color: #000;
                                            }
                                            .page-layout-gudm .center {
                                                text-align: center;
                                            }

                                            .page-layout-gudm h1 {
                                                font-size: 14pt;
                                                margin: 0 0 8px;
                                                font-weight: bold;
                                            }

                                            .page-layout-gudm h2 {
                                                font-size: 13pt;
                                                margin: 0 0 8px;
                                                font-weight: bold;
                                            }

                                            .page-layout-gudm h3 {
                                                font-size: 14pt;
                                                margin: 12px 0 16px;
                                                font-weight: bold;
                                            }

                                            .page-layout-gudm .dots {
                                                display: inline;
                                                border-bottom: 1px dotted #000;
                                            }

                                            .page-layout-gudm .checkbox {
                                                display: inline-block;
                                                width: 13px;
                                                height: 13px;
                                                border: 1px solid #000;
                                                vertical-align: middle;
                                                margin: 0 4px;
                                            }

                                            .page-layout-gudm .checkbox.checked::after {
                                                content: "✓";
                                                display: block;
                                                color: #000;
                                                font-weight: bold;
                                                font-size: 12px;
                                                line-height: 11px;
                                                text-align: center;
                                            }

                                            .page-layout-gudm p.line {
                                                margin: 8px 0;
                                            }

                                            .page-layout-gudm .meter-hh { border-right: 0 !important; }
                                            .page-layout-gudm .meter-mm { border-left: 0 !important; }

                                            .page-layout-gudm table {
                                                width: 100%;
                                                border-collapse: collapse;
                                                margin: 10px 0;
                                            }

                                            .page-layout-gudm td,
                                            .page-layout-gudm th {
                                                border: 1px solid #000;
                                                padding: 10px 8px;
                                                vertical-align: middle;
                                                font-size: 11pt;
                                            }

                                            .page-layout-gudm th {
                                                font-weight: bold;
                                                text-align: center;
                                                height: 45px;
                                            }

                                            .page-layout-gudm td {
                                                height: 40px;
                                            }

                                            .page-layout-gudm .num {
                                                width: 6%;
                                                text-align: center;
                                            }

                                            .page-layout-gudm .blank {
                                                height: 45px;
                                            }

                                            .page-layout-gudm .sig {
                                                display: -webkit-flex;
                                                display: flex;
                                                -webkit-justify-content: space-between;
                                                justify-content: space-between;
                                                margin-top: 35px;
                                                text-align: center;
                                            }

                                            .page-layout-gudm .sig div {
                                                width: 32%;
                                                padding-top: 45px;
                                                font-size: 11pt;
                                            }

                                            .page-layout-gudm .note {
                                                margin-top: 20px;
                                                padding-top: 10px;
                                                border-top: 1px solid #000;
                                                font-size: 10pt;
                                                line-height: 1.5;
                                            }

                                            .page-layout-gudm .flexrow {
                                                display: -webkit-flex;
                                                display: flex;
                                                -webkit-flex-wrap: nowrap;
                                                flex-wrap: nowrap;
                                            }

                                            .page-layout-gudm .flexrow > *:not(:last-child) {
                                                margin-right: 10px;
                                            }

                                            .page-layout-gudm .flexrow > table {
                                                margin: 8px 0;
                                            }
                                            /* Dedicated full-page A4 certificate used only by SMT Surat. */
                                            /* Exact A4 sizing for the supplied Performance certificate reference. */
                                            .page-layout-gudm.sm-techno-certificate {
                                                width: 210mm;
                                                min-height: 297mm;
                                                margin: 0 auto;
                                            }

                                            .page-layout-gudm.sm-techno-certificate .page {
                                                width: 100%;
                                                min-height: 297mm;
                                                padding: 16mm 14.5mm 5mm 23.4mm;
                                                margin: 0;
                                                font-family: "Noto Sans Gujarati", "Shruti", "Nirmala UI", sans-serif;
                                                font-size: 7.56pt;
                                                line-height: 1.35;
                                                word-spacing: 0.35px;
                                                box-sizing: border-box;
                                            }
                                            .page-layout-gudm.sm-techno-certificate h1,
                                            .page-layout-gudm.sm-techno-certificate h2,
                                            .page-layout-gudm.sm-techno-certificate h3 {
                                                font-family: "Noto Sans Gujarati", "Shruti", "Nirmala UI", sans-serif;
                                                font-weight: 400;
                                                letter-spacing: 0;
                                                word-spacing: 0;
                                                line-height: 1.35;
                                                white-space: nowrap;
                                                text-rendering: geometricPrecision;
                                            }

                                            .page-layout-gudm.sm-techno-certificate h1 {
                                                font-size: 14.04pt;
                                                letter-spacing: 0.12px;
                                                word-spacing: 2.25px;
                                                margin: 0 0 8px;
                                            }

                                            .page-layout-gudm.sm-techno-certificate h2 {
                                                font-size: 12pt;
                                                letter-spacing: 1.0px;
                                                word-spacing: 1.0px;
                                                margin: 0 0 5px;
                                            }
                                                

                                            .page-layout-gudm.sm-techno-certificate h3 {
                                                font-size: 11.04pt;
                                                letter-spacing: 0.08px;
                                                word-spacing: 1px;
                                                margin: 0 0 2px;
                                                text-decoration: none;
                                            }

                                            .page-layout-gudm.sm-techno-certificate p.line {
                                                margin: 5px 0;
                                                line-height: 1.45;
                                            }

                                            .page-layout-gudm.sm-techno-certificate table {
                                                margin: 0;
                                                table-layout: fixed;
                                            }

                                            .page-layout-gudm.sm-techno-certificate td,
                                            .page-layout-gudm.sm-techno-certificate th {
                                                padding: 2px 6px;
                                                font-size: 7.56pt;
                                            }
                                            /* Match the taller first two activity rows in the reference certificate. */
                                            .page-layout-gudm.sm-techno-certificate .page > table:first-of-type tbody > tr:nth-child(1) > td,
                                            .page-layout-gudm.sm-techno-certificate .page > table:first-of-type tbody > tr:nth-child(2) > td {
                                                padding-top: 4px;
                                                padding-bottom: 4px;
                                            }

                                            .page-layout-gudm.sm-techno-certificate td.center {
                                                white-space: normal;
                                                overflow: visible;
                                            }

                                            .page-layout-gudm.sm-techno-certificate td.certificate-time {
                                                white-space: nowrap;
                                                word-spacing: 0;
                                            }

                                            .page-layout-gudm.sm-techno-certificate .vehicle-number {
                                                display: inline-block;
                                                min-width: 135px;
                                                margin-right: 4px;
                                                text-align: center;
                                            }
                                            .page-layout-gudm.sm-techno-certificate .checkbox {
                                                width: 13px;
                                                height: 13px;
                                                margin: 0 4px;
                                            }

                                            .page-layout-gudm.sm-techno-certificate th,
                                            .page-layout-gudm.sm-techno-certificate td {
                                                height: auto;
                                            }

                                            .page-layout-gudm.sm-techno-certificate .blank {
                                                height: 12px;
                                            }

                                            .page-layout-gudm.sm-techno-certificate .sig {
                                                margin-top: 8px;
                                            }

                                            .page-layout-gudm.sm-techno-certificate .sig div {
                                                padding-top: 0;
                                                font-size: 7.56pt;
                                            }

                                            .page-layout-gudm.sm-techno-certificate .note {
                                                margin-top: 10px;
                                                padding-top: 4px;
                                                font-size: 7.56pt;
                                            }

                                            .page-layout-gudm.sm-techno-certificate .flexrow > *:not(:last-child) {
                                                margin-right: 2%;
                                            }

                                            .page-layout-gudm.sm-techno-certificate .flexrow > table {
                                                margin: 0;
                                            }
                                            .page-layout-gudm.sm-techno-certificate .flexrow td,
                                            .page-layout-gudm.sm-techno-certificate .flexrow th {
                                                font-size: 7.56pt;
                                            }
                                            /* Reference-certificate column proportions and boxed sub-sections. */
                                            .page-layout-gudm.sm-techno-certificate .page > table:first-of-type th:nth-child(1) {
                                                width: 5.2% !important;
                                            }
                                            .page-layout-gudm.sm-techno-certificate .page > table:first-of-type th:nth-child(2),
                                            .page-layout-gudm.sm-techno-certificate .page > table:first-of-type th:nth-child(5) {
                                                width: 20.3% !important;
                                            }
                                            .page-layout-gudm.sm-techno-certificate .page > table:first-of-type th:nth-child(3),
                                            .page-layout-gudm.sm-techno-certificate .page > table:first-of-type th:nth-child(6) {
                                                width: 13% !important;
                                            }

                                            .page-layout-gudm.sm-techno-certificate .page > table:first-of-type th:nth-child(4) {
                                                width: 13.7% !important;
                                            }

                                            .page-layout-gudm.sm-techno-certificate .page > table:first-of-type th:nth-child(7) {
                                                width: 14.5% !important;
                                            }

                                            .page-layout-gudm.sm-techno-certificate .flexrow > div:first-child {
                                                width: 51% !important;
                                                box-sizing: border-box;
                                            }

                                            .page-layout-gudm.sm-techno-certificate .flexrow > div:last-child {
                                                width: 47% !important;
                                                box-sizing: border-box;
                                            }

                                            .page-layout-gudm.sm-techno-certificate .section-title {
                                                margin: 4px 0;
                                                font-size: 7.56pt;
                                                font-weight: 400;
                                                line-height: 1.35;
                                                text-align: left;
                                                white-space: nowrap;
                                            }

                                            .page-layout-gudm.sm-techno-certificate .staff-name-row {
                                                display: -webkit-flex;
                                                display: flex;
                                                margin: 5px 0;
                                                font-size: 11px;
                                                line-height: 1.35;
                                            }

                                            .page-layout-gudm.sm-techno-certificate .staff-name-row > span:not(:last-child) {
                                                margin-right: 6px;
                                            }

                                            .page-layout-gudm.sm-techno-certificate .staff-name-row > span {
                                                -webkit-box-flex: 1;
                                                -webkit-flex: 1 1 0%;
                                                flex: 1 1 0%;
                                                min-width: 0;
                                                overflow-wrap: break-word;
                                                word-break: break-word;
                                            }

                                            .page-layout-gudm.sm-techno-certificate .flexrow > div:first-child th:nth-child(1) {
                                                width: 11% !important;
                                            }

                                            .page-layout-gudm.sm-techno-certificate .flexrow > div:first-child th:nth-child(2) {
                                                width: 57% !important;
                                            }

                                            .page-layout-gudm.sm-techno-certificate .flexrow > div:first-child th:nth-child(3) {
                                                width: 32% !important;
                                            }

                                            .page-layout-gudm.sm-techno-certificate .flexrow > div:first-child td:nth-child(3) {
                                                width: 18% !important;
                                            }

                                            .page-layout-gudm.sm-techno-certificate .flexrow > div:first-child td:nth-child(4) {
                                                width: 14% !important;
                                            }

                                            @media print {
                                                .page-layout-gudm {
                                                    background: #fff;
                                                }

                                                .page-layout-gudm .page {
                                                    margin: 0;
                                                    padding: 0;
                                                    box-shadow: none;
                                                }
                                            }
                                            `}
            </style>

            <div className="page">
                <div className="center">
                    <h1>ગુજરાત શહેરી વિકાસ મિશન, ગાંધીનગર</h1>
                                                <h2><span style={{ padding: '0 0px' }}>{municipalityName}</span> નગરપાલિકા</h2>
                    <h3>પરફોર્મન્સ સર્ટીફીકેટ / લોગ શીટ</h3>
                </div>
                <br></br>
                <p className="line">મે. મેસ્ટ્રો વેસ્ટ હેન્ડલીંગ પ્રા.લી. ને સર્ટીફીકેટ આપવામાં આવે છે કે, ડીશીલ્ટીંગ મશીન રજીસ્ટ્રેશન નંબર <span className="dots" style={{ padding: '0 5px' }}>{data?.VehicleNo}</span> GVW.18.5T <span className={`checkbox ${isEighteenPointFiveTon ? 'checked' : ''}`}></span> 7.0T <span className={`checkbox ${isSevenTon ? 'checked' : ''}`}></span></p>
                <p className="line">દ્વારા તા. <span className="dots" style={{ padding: '0 5px' }}>{data?.BookingDate}</span> ના રોજ <span className="dots" style={{ padding: '0 5px' }}>{data?.Shift}</span> શીફ્ટમાં <span className="dots" style={{ padding: '0 5px' }}>{data?.TotalHours}:{data?.TotalMinutes}</span> કલાક માટે નીચે મુજબની વિગતથી કામગીરી કરેલ છે.</p>

                <p className="line">૧. ઝોનઃ- <span className="dots" style={{ padding: '0 5px' }}>{data?.ZoneName}</span> વોર્ડઃ- <span className="dots" style={{ padding: '0 5px' }}>{data?.WardName}</span>, કામગીરીનું સ્થળઃ- <span className="dots" style={{ padding: '0 5px' }}>{data?.BookingStartAddress }</span></p>
                <p className="line">૨. પાર્કિંગનું સ્થળઃ- <span className="dots" style={{ padding: '0 5px' }}>{data?.ParkingLocationName}</span> પાર્કિંગના સ્થળ અને કામગીરીના સ્થળ વચ્ચેનું અંતર <span className="dots" style={{ padding: '0 5px' }}>{totalDifferenceOdometer}</span> કિ.મી.</p>
                <p className="line">૩. કામગીરીની વિગતઃ-</p>

                <table>
                    <thead>
                        <tr>
                            <th className="num">ક્રમાંક</th>
                            <th style={{ width: '21%' }}>વિગત</th>
                            <th style={{ width: '15%' }}>સમય</th>
                            <th style={{ width: '11%' }}>ઓડોમીટર રીડીંગ</th>
                            <th style={{ width: '21%' }}>વિગત</th>
                            <th style={{ width: '15%' }}>સમય</th>
                            <th style={{ width: '11%' }}>ઓડોમીટર રીડીંગ</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr className="blank">
                            <td className="num">૧.</td>
                            <td>મશીન પાર્કિંગથી નિકળ્યાનો સમય</td>
                            <td className="center certificate-time">
                                {formatDateTimeWithAMPM(data?.VehicleLeaveParkingTime || data?.VehicleLeaveParkingTime2).date}
                                <br />
                                {formatDateTimeWithAMPM(data?.VehicleLeaveParkingTime || data?.VehicleLeaveParkingTime2).time}
                            </td>
                            <td className="center">{data?.VehicleLeaveParkingOdometer}</td>
                            <td>મશીન કામગીરીના સ્થળ ઉપર આવ્યાનો સમય</td>
                            <td className="center certificate-time">
                                {formatDateTimeWithAMPM(data?.VehicleReachedLocationTime || data?.VehicleReachedLocationTime2).date}
                                <br />
                                {formatDateTimeWithAMPM(data?.VehicleReachedLocationTime || data?.VehicleReachedLocationTime2).time}
                            </td>
                            <td className="center">{data?.VehicleReachedLocationOdometer}</td>
                        </tr>
                        <tr>
                            <td className="num">૨.</td>
                            <td>મશીન કામગીરીના સ્થળ ઉપરથી નિકળ્યાનો સમય</td>
                            <td className="center certificate-time">
                                {formatDateTimeWithAMPM(data?.VehicleReturnLocationTime2).date}
                                <br />
                                {formatDateTimeWithAMPM(data?.VehicleReturnLocationTime2).time}
                            </td>
                            <td className="center">{data?.VehicleReturnLocationOdometer}</td>
                            <td>મશીન પાર્કિંગ ઉપર પહોંચ્યાનો સમય</td>
                            <td className="center certificate-time">
                                {formatDateTimeWithAMPM(data?.VehicleReachedParkingTime2).date}
                                <br />
                                {formatDateTimeWithAMPM(data?.VehicleReachedParkingTime2).time}
                            </td>
                            <td className="center">{data?.VehicleReachedParkingOdometer}</td>
                        </tr>
                        <tr className="blank">
                            <td className="num">૩.</td>
                            <td>કુલ સમય</td>
                            <td colSpan="2" className="center"><span style={{ padding: '0 5px' }}>{data?.TotalHours}</span> કલાક <span style={{ padding: '0 5px' }}>{data?.TotalMinutes}</span> મીનીટ</td>
                            <td>કુલ ઓડોમીટર રીડીંગ</td>
                            <td colSpan="2" className="center">{data?.TotalKiloMeter}</td>
                        </tr>
                        <tr>
                            <td className="num">૪.</td>
                            <td>વિગત</td>
                            <td className="center meter-hh">HH</td><td className="center meter-mm">MM</td>
                            <td>વિગત</td>
                            <td className="center meter-hh">HH</td><td className="center meter-mm">MM</td>
                        </tr>
                        <tr className="blank">
                            <td className="num" rowSpan="3">૫.</td>
                            <td rowSpan="3">મશીનની જેટીંગની કામગીરી શરૂ કર્યાનું રીડીંગ<br /><b>Jetting Starting Meter Reading</b></td>
                            <td className="left meter-hh">૧  {data?.JettingStartMeterReading ? data?.JettingStartMeterReading.split(':')[0] : ''}</td>
                            <td className="center meter-mm">{data?.JettingStartMeterReading ? data?.JettingStartMeterReading.split(':')[1] : ''}</td>
                            <td rowSpan="3">મશીનની જેટીંગની કામગીરી પૂર્ણ કર્યાનું રીડીંગ<br /><b>Jetting Finish Meter Reading</b></td>
                            <td className="left meter-hh">૧  {data?.JettingFinishMeterReading ? data?.JettingFinishMeterReading.split(':')[0] : ''}</td>
                            <td className="center meter-mm">{data?.JettingFinishMeterReading ? data?.JettingFinishMeterReading.split(':')[1] : ''}</td>
                        </tr>
                        <tr className="blank">
                            <td className="left meter-hh">૨</td>
                            <td className="meter-mm"></td>
                            <td className="left meter-hh">૨</td>
                            <td className="meter-mm"></td>
                        </tr>
                        <tr className="blank">
                            <td className="left meter-hh">૩</td>
                            <td className="meter-mm"></td>
                            <td className="left meter-hh">૩</td>
                            <td className="meter-mm"></td>
                        </tr>
                        <tr className="blank">
                            <td className="num" rowSpan="3">૬.</td>
                            <td rowSpan="3">મશીનની સકશનની કામગીરી શરૂ કર્યાના રનીંગ અવર્સના રીડીંગ<br /><b>Suction Pump Starting Meter Reading</b></td>
                            <td className="left meter-hh">૧  {data?.SuctionPumpStartMeterReading ? data?.SuctionPumpStartMeterReading.split(':')[0] : ''}</td>
                            <td className="center meter-mm">{data?.SuctionPumpStartMeterReading ? data?.SuctionPumpStartMeterReading.split(':')[1] : ''}</td>
                            <td rowSpan="3">મશીનની સકશનની કામગીરી પૂર્ણ કર્યાના રનીંગ અવર્સના રીડીંગ<br /><b>Suction Finish Meter Reading</b></td>
                            <td className="left meter-hh">૧  {data?.SuctionPumpFinishMeterReading ? data?.SuctionPumpFinishMeterReading.split(':')[0] : ''}</td>
                            <td className="center meter-mm">{data?.SuctionPumpFinishMeterReading ? data?.SuctionPumpFinishMeterReading.split(':')[1] : ''}</td>
                        </tr>
                        <tr className="blank">
                            <td className="left meter-hh">૨</td>
                            <td className="meter-mm"></td>
                            <td className="left meter-hh">૨</td>
                            <td className="meter-mm"></td>
                        </tr>
                        <tr className="blank">
                            <td className="left meter-hh">૩</td>
                            <td className="meter-mm"></td>
                            <td className="left meter-hh">૩</td>
                            <td className="meter-mm"></td>
                        </tr>
                        <tr>
                            <td className="num">૭.</td>
                            <td colSpan="3">કુલ જેટીંગ સમયઃ- <span className="dots" style={{ minWidth: '40px' }}>{data?.JettingTotalHours}</span> કલાક <span className="dots" style={{ minWidth: '40px' }}>{data?.JettingTotalMinutes}</span> મીનીટ</td>
                            <td colSpan="3">કુલ સકશન સમયઃ- <span className="dots" style={{ minWidth: '40px' }}>{data?.SuctionPumpTotalHours}</span> કલાક <span className="dots" style={{ minWidth: '40px' }}>{data?.SuctionPumpTotalMinutes}</span> મીનીટ</td>
                        </tr>
                    </tbody>
                </table>

                <table>
                    <thead>
                        <tr>
                            <th className="num">ક્રમાંક</th>
                            <th style={{ width: '40%' }}>ડ્રેનેજ લાઈન સાઈઝ</th>
                            <th style={{ width: '25%' }}>ટેન્ડરના KPI મુજબનો ટાર્ગેટ</th>
                            <th style={{ width: '29%' }}>કામગીરીના સ્થળ ઉપર થયેલ ખરેખર કામગીરી</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr className="blank"><td className="num">૧.</td><td>૩૦૦ એમ.એમ. ડાયામીટર</td><td className="center">૬૦ મીટર પ્રતિ કલાક</td><td className="center">{data?.DrainageLine150to300mmDiaMeter}</td></tr>
                        <tr className="blank"><td className="num">૨.</td><td>૩૦૧ એમ.એમ. થી ૬૦૦ એમ.એમ. ડાયામીટર</td><td className="center">૪૦ મીટર પ્રતિ કલાક</td><td className="center">{data?.DrainageLine301to600mmDiaMeter}</td></tr>
                        <tr className="blank"><td className="num">૩.</td><td>૬૦૧ એમ.એમ. થી ૯૦૦ એમ.એમ. ડાયામીટર</td><td className="center">૩૦ મીટર પ્રતિ કલાક</td><td className="center">{data?.DrainageLine601to900mmDiaMeter}</td></tr>
                        <tr className="blank"><td className="num">૪.</td><td>૯૦૧ એમ.એમ. થી ૨૧૦૦ એમ.એમ. ડાયામીટર</td><td className="center">૧૦ મીટર પ્રતિ કલાક</td><td className="center">{data?.DrainageLine901to1200mmDiaMeter}</td></tr>
                    </tbody>
                </table>

                <p className="line">૪. સદર વોર્ડની ડ્રેનેજ લાઈનની ડીશીલ્ટીંગ / ડીચોકીંગની નીચે મુજબની કામગીરી કરેલ છે તથા કુલ <span className="dots" style={{ padding: '0 5px' }}>{data?.TotalDrainageMeter || data?.TotalMeter}</span> મીટર ડ્રેનેજ લાઈન અને <span className="dots" style={{ padding: '0 5px' }}>{data?.TotalNoOfDranageLine}</span> નંગ મશીનહોલની સફાઈની કામગીરી કરેલ છે.</p>

                <div className="flexrow" style={{ WebkitAlignItems: 'stretch', alignItems: 'stretch' }}>
                    <div style={{ display: '-webkit-flex', WebkitFlexDirection: 'column', flexDirection: 'column' }}>
                        <div className="section-title">૫અ. કામગીરીના સ્થળ ઉપર મળેલ મશીનના ટેકનીકલ પેરામીટરની વિગતઃ-</div>
                        <table style={{ width: '100%', height: '100%' }}>
                            <thead>
                                <tr><th style={{ width: '12%', textAlign: 'center' }}>ક્રમાંક</th><th style={{ width: '63%' }}>ટેકનીકલ પેરામીટર</th><th colSpan="2">ટેન્ડર મુજબ જરૂરીયાત</th></tr>
                            </thead>
                            <tbody>
                                <tr><td className="num">૧.</td><td>જેટીંગ સિસ્ટમ પ્રેશર મીટર</td><td className="center">હા / ના</td><td className="center">{yesNoText(data?.IsJettingSystemAvailable, data?.IsJettingSystemPressureMeter)}</td></tr>
                                <tr><td className="num">૨.</td><td>સકશન સિસ્ટમ પ્રેશર મીટર</td><td className="center">હા / ના</td><td className="center">{yesNoText(data?.IsSuctionSystemAvailable, data?.IsSuctionSystemPressureMeter)}</td></tr>
                                <tr><td className="num">૩.</td><td>સકશન હોઝ ૪’’ ડાયામીટર – ૩ મીટર લંબાઈ</td><td className="center">૧૦ નંગ</td><td className="center">{data?.IsSuctionHose4Diameter3MeterLong}</td></tr>
                                <tr><td className="num">૪.</td><td>જેટીંગ હોઝ ૩૨ એમ.એમ. ડાયા, ૧૨૦ મી. લંબાઈ</td><td className="center">હા / ના</td><td className="center">{yesNoText(data?.IsJettingHoseAvailable, data?.IsJettingHose32Diameter120MeterLong)}</td></tr>
                                
                                



                                
                                <tr><td className="num">૬.</td><td>ડી શીલ્ટીંગ નોઝલ – ફ્લેટ નોઝલ</td><td className="center">૧ નંગ</td><td className="center">{data?.DeSiltingNozzleFlatNozzle}</td></tr>
                                <tr><td className="num">૭.</td><td>બેલેન્સ નોઝલ – બોમ્બ નોઝલ</td><td className="center">૧ નંગ</td><td className="center">{data?.BalanceNozzleBombNozzle}</td></tr>
                            </tbody>
                        </table>
                    </div>

                    <div style={{ display: '-webkit-flex', WebkitFlexDirection: 'column', flexDirection: 'column' }}>
                        <div className="section-title">૫બ. મશીનની કામગીરી બંધ રહ્યાની વિગત</div>
                        <table style={{ width: '100%', height: '100%' }}>
                            <thead>
                                <tr><th style={{ width: '25%' }}>સમય</th><th style={{ width: '25%' }}>ક્યાં થી</th><th style={{ width: '25%' }}>ક્યાં સુધી</th><th style={{ width: '25%' }}>કુલ કલાક</th></tr>
                            </thead>
                            <tbody>
                                <tr className="blank"><td className="center">રીસેસ</td><td className="center">{data?.LunchStartTime}</td><td className="center">{data?.LunchEndTime}</td><td className="center">{data?.TotalLunchTime}</td></tr>
                                <tr className="blank" style={{ verticalAlign: 'top' }}><td className="center">અન્ય કારણસર</td><td className="center">{data?.MachineNotWorkFromTime || data?.MachineNotWorkFromTime2}</td><td className="center">{data?.MachineNotWorkToTime || data?.MachineNotWorkToTime2}</td><td className="center">{data?.MachineNotWorkTotalTime}</td></tr>
                                <tr><td colSpan="4" style={{ fontSize: '8pt' }}>નગરપાલિકા દ્વારા પુરા પાડેલ ચોખ્ખા પાણીના રૂ. ૧૦ પ્રતિ કિલો લીટર લેખે નગરપાલિકામાં જમા લીધેલ રૂ.</td></tr>
                                <tr className="blank"><td colSpan="4" style={{ padding: 0 }}><div style={{ display: '-webkit-flex', display: 'flex', width: '100%' }}><span style={{ WebkitFlex: '1', flex: '1', padding: '4px', borderRight: '1px solid black' }}>કિલો લીટર: {data?.Kiloliter}</span><span style={{ WebkitFlex: '1', flex: '1', padding: '4px', borderRight: '1px solid black' }}>રૂ.: {data?.KiloliterRS}</span><span style={{ WebkitFlex: '1', flex: '1', padding: '4px' }}>પહોંચ નં: {data?.PahochNo}</span></div></td></tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <p className="line">૬. સ્ટાફને જરૂરી તમામ સુરક્ષા સાધનો (PPE) આપેલ છે? {renderYesNoChoices(data?.PPEProvided, data?.IsPPEGivenToEmployee)}</p>
                <p className="line">૭. Sludge નું ડીસપોઝલ નગરપાલિકા એ બતાવેલ જગ્યાએ ખાલી કરેલ છે? {renderYesNoChoices(data?.SludgeDisposalArea, data?.IsSludgeDisposalByMuncipality)}</p>
                <p className="line">૮. અસાધારણ પરિસ્થિતિની વિગત : અસાધારણ પરિસ્થિતિ <span className="dots" style={{ padding: '0 5px' }}>{data?.BookingCompleteRemarks}</span></p>
                <p className="line">સ્થળ <span className="dots" style={{ padding: '0 5px' }}>{data?.Locality}</span> મશીનહોલ (MH) ની સંખ્યા <span className="dots" style={{ padding: '0 5px' }}>{bookingDefects.length}</span></p>
                <p className="line">૯. અન્ય ફોલ્ટની કામગીરીની વિગતોઃ- <span className="dots" style={{ padding: '0 5px' }}>{lastDefectRemark == null || lastDefectRemark === 'null' ? '' : lastDefectRemark}</span></p>
                <p className="line">૧૦. સાઈટ ઉપર મશીન ઉપર કોન્ટ્રાકટરના હાજર સ્ટાફની વિગતઃ-</p>
                <div className="staff-name-row">
                    <span>(i) ડ્રાઈવરનું નામઃ- <span className="dots" style={{ padding: '0 5px' }}>{data?.DriverFullName}</span></span>
                    <span>(ii) ઓપરેટરનું નામઃ- <span className="dots" style={{ padding: '0 5px' }}>{data?.MultipleHelperName}</span></span>
                    <span>(iii) સુપરવાઈઝરનું નામઃ- <span className="dots" style={{ padding: '0 5px' }}>{isEmailCondition ? data?.AssignSupervisorFullName : ''}</span></span>
                </div>

                <div className="sig" style={{ marginTop: '30px' }}>
                    <div style={{ textAlign: 'left' }}>મશીન સુપરવાઈઝર ઇન્ચાર્જની સહી<br />નામ:-</div>
                    <div style={{ textAlign: 'left' }}>નગરપાલિકા સુપરવાઈઝરની સહી<br />નામ:-</div>
                    <div style={{ textAlign: 'left' }}>નગરપાલિકા એન્જીનીયરની સહી<br />નામ:-</div>
                </div>
                <div className="note">
                    <b>Special Note:-</b> As per Hon. High Court order in any case, contractor is not allowed to enter/work inside the machinehole (MH) or sewer lines. Proper training &amp; knowledge shall be provided to all personals engaged for work by contractor.
                </div>
            </div>
        </div>
    );

    return (
        <div>
            {pdfUrl && (
                <a
                    href={pdfUrl}
                    download={pdfFileName}
                    style={{ position: 'fixed', top: '16px', right: '16px', zIndex: 10000, display: 'inline-flex', alignItems: 'center', gap: '9px', padding: '8px 14px 8px 8px', background: '#f7fbff', color: '#0f5fb3', border: '1px solid #c9def5', borderRadius: '8px', fontFamily: 'Arial, sans-serif', fontSize: '14px', fontWeight: 600, textDecoration: 'none', boxShadow: '0 3px 10px rgba(25,118,210,.16)' }}
                >
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', background: '#1976d2', color: '#fff', borderRadius: '6px' }}>
                        <i className="far fa-file-pdf" style={{ fontSize: '15px' }}></i>
                    </span>
                    Download PDF
                </a>
            )}
            {renderCertificate(false)}
            {renderCertificate(true)}
        </div>
    );
};

export default LogsheetPrintView;
